import { useState, type ReactNode } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import { useDroppedOrder } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';

export interface PracticeLibraryOrderProps {
  cards: RoleplayCard[];
  /* The whole library, in the order it now stands in. */
  onReorder: (ids: string[]) => void;
  /* One card, with the way to move it a place from the keyboard. */
  children: (card: RoleplayCard, onMove: (direction: -1 | 1) => void) => ReactNode;
}

/**
 * The library as its admins arrange it: drag a card by its grip, or move it a
 * place with the arrows on the grip. The whole order is handed back, so what
 * is stored is the list on the screen.
 */
export function PracticeLibraryOrder({
  cards: handedIn,
  onReorder,
  children,
}: PracticeLibraryOrderProps) {
  /*
   * A few pixels of slack, or a plain click on the grip already starts a drag.
   * The pointer is the only sensor: the arrows are answered on the grip.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  /* What the last keyboard move did, for a reader who cannot see it happen. */
  const [moved, setMoved] = useState('');

  const [cards, holdOrder] = useDroppedOrder(handedIn);
  const ids = cards.map((card) => card.id);

  function reorder(order: string[]) {
    holdOrder(order);
    onReorder(order);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (over === null || active.id === over.id) return;

    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;

    reorder(arrayMove(ids, from, to));
  }

  function moveBy(cardId: string, direction: -1 | 1) {
    const from = ids.indexOf(cardId);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= ids.length) return;

    setMoved(
      m.practice_drag_over({
        title: titleOf(cardId),
        position: to + 1,
        total: ids.length,
      }),
    );
    reorder(arrayMove(ids, from, to));
  }

  function titleOf(id: UniqueIdentifier): string {
    return cards.find((card) => String(id) === card.id)?.title ?? '';
  }

  function placeOf(id: UniqueIdentifier): number {
    return ids.indexOf(String(id)) + 1;
  }

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: m.practice_drag_instructions(),
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      m.practice_drag_start({
        title: titleOf(active.id),
        position: placeOf(active.id),
        total: ids.length,
      }),
    /* Silent over its own place, which it is the instant it is picked up. */
    onDragOver: ({ active, over }) =>
      over && over.id !== active.id
        ? m.practice_drag_over({
            title: titleOf(active.id),
            position: placeOf(over.id),
            total: ids.length,
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? m.practice_drag_end({
            title: titleOf(active.id),
            position: placeOf(over.id),
            total: ids.length,
          })
        : m.practice_drag_cancel({ title: titleOf(active.id) }),
    onDragCancel: ({ active }) => m.practice_drag_cancel({ title: titleOf(active.id) }),
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements, screenReaderInstructions }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {cards.map((card) => children(card, (direction) => moveBy(card.id, direction)))}
      </SortableContext>

      {/* dnd-kit never announces a keyboard move: it is over when it is made. */}
      <p aria-live='polite' className='sr-only'>
        {moved}
      </p>
    </DndContext>
  );
}
